// Character sheet drawer component with character selector

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import '../shared/token-thumbnail.js';
import './character-sheet.js';

/**
 * Character sheet drawer component
 * Wraps character sheet with character selector for multiple PCs
 */
class CharacterSheetDrawer extends BaseComponent {
  static properties = {
    characters: { type: Array },
    selectedCharacterId: { type: String, attribute: 'selected-character-id' },
    isGM: { type: Boolean, attribute: 'is-gm' },
    currentUserId: { type: String, attribute: 'current-user-id' },
    _internalSelectedId: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .character-sheet-drawer {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .character-selector {
      display: flex;
      gap: 8px;
      padding: 12px;
      border-bottom: 1px solid #444;
      background: rgba(0, 0, 0, 0.3);
      flex-shrink: 0;
    }

    .character-selector token-thumbnail {
      cursor: pointer;
      transition:
        transform 0.2s,
        box-shadow 0.2s;
    }

    .character-selector token-thumbnail:hover {
      transform: scale(1.05);
    }

    .character-selector token-thumbnail.selected {
      box-shadow: 0 0 8px rgba(74, 158, 255, 0.5);
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Character>} */
    this.characters = [];
    /** @type {string|null} */
    this.selectedCharacterId = null;
    this.isGM = false;
    this.currentUserId = '';
    /** @type {string|null} */
    this._internalSelectedId = null;
  }

  /** @param {Map<string, unknown>} changedProps */
  willUpdate(changedProps) {
    // Initialize selected character when characters change
    if (changedProps.has('characters') && this.characters.length > 0 && !this._effectiveSelectedId) {
      const first = this.characters[0];
      if (!first) throw new Error('Expected at least one character');
      this._internalSelectedId = first.id;
    }
  }

  get _effectiveSelectedId() {
    return this.selectedCharacterId ?? this._internalSelectedId;
  }

  get _selectedCharacter() {
    if (this._effectiveSelectedId) {
      return this.characters.find((c) => c.id === this._effectiveSelectedId) || this.characters[0] || null;
    }
    return this.characters[0] || null;
  }

  /** @param {string} characterId */
  _handleCharacterSelect(characterId) {
    this._internalSelectedId = characterId;
    this.dispatchEvent(
      new CustomEvent('update:selected-character-id', {
        detail: characterId,
        bubbles: true,
      }),
    );
  }

  /** @param {CustomEvent} e */
  _handleCharacterUpdate(e) {
    if (!this._selectedCharacter) return;
    this.dispatchEvent(
      new CustomEvent('update:character', {
        detail: {
          characterId: this._selectedCharacter.id,
          updates: e.detail,
        },
        bubbles: true,
      }),
    );
  }

  /** @param {CustomEvent} e */
  _handleReassignPlayer(e) {
    this.dispatchEvent(
      new CustomEvent('reassign-player', {
        detail: e.detail,
        bubbles: true,
      }),
    );
  }

  /** @param {CustomEvent} e */
  _handleEquipItem(e) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('equip-item', {
        detail: e.detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <div class="character-sheet-drawer">
        ${
          this.characters.length > 1
            ? html`
              <div class="character-selector">
                ${this.characters.map(
                  (character) => html`
                    <token-thumbnail
                      image-url="${character.token?.imageUrl || ''}"
                      name="${character.name}"
                      has-circle-border
                      border-color="${character.id === this._effectiveSelectedId ? '#4a9eff' : '#444'}"
                      size="70"
                      class="${character.id === this._effectiveSelectedId ? 'selected' : ''}"
                      @click="${() => this._handleCharacterSelect(character.id)}"
                    ></token-thumbnail>
                  `,
                )}
              </div>
            `
            : ''
        }
        ${
          this._selectedCharacter
            ? html`
              <character-sheet
                .character="${this._selectedCharacter}"
                ?is-gm="${this.isGM}"
                current-user-id="${this.currentUserId}"
                @update:character="${this._handleCharacterUpdate}"
                @reassign-player="${this._handleReassignPlayer}"
                @equip-item="${this._handleEquipItem}"
              ></character-sheet>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('character-sheet-drawer', CharacterSheetDrawer);
